import React, { useCallback, useContext, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { Header, Form, Segment, Label, Icon, Popup } from 'semantic-ui-react'
import DataContext from '../../../contexts/DataContext'
import {
  PROFESSIONAL_REGISTRATION,
  PROFESSIONAL_REGISTRATION_TOOLTIP,
} from '../../../constants/professionalRegistration'

const ProfessionalRegistration = ({
  country,
  professionalRegistrations,
  setProfessionalRegistrations,
  noRegistration,
  setNoRegistration,
}) => {
  const { countries = [] } = useContext(DataContext)
  const history = useHistory()
  const countryCode = countries
    .find((c) => c.name === country)
    ?.code?.toLowerCase()
  const registrationConfig = PROFESSIONAL_REGISTRATION[countryCode]

  const goToProfileCountry = (e) => {
    e.preventDefault()
    history.push('/profile')
    // Runs after the route renders and after ScrollToTop resets the scroll position.
    requestAnimationFrame(() =>
      document
        .getElementById('country-field')
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    )
  }

  const professionalRegistration = professionalRegistrations?.find(
    (registration) => registrationConfig?.types.includes(registration.type)
  )

  const hasValidType = registrationConfig?.types.includes(
    professionalRegistration?.type
  )
  const registration = hasValidType
    ? professionalRegistration
    : { type: registrationConfig?.types[0], number: '' }

  const setProfessionalRegistration = useCallback(
    (nextRegistration) => {
      // The UI collects a single registration, so it replaces any previously stored one.
      setProfessionalRegistrations(nextRegistration ? [nextRegistration] : [])
    },
    [setProfessionalRegistrations]
  )

  useEffect(() => {
    // The country no longer collects a registration, so drop any ID kept from the previous one.
    if (!registrationConfig) {
      if (professionalRegistrations?.length) {
        setProfessionalRegistrations([])
        setNoRegistration(false)
      }
      return
    }
    if (!hasValidType && !noRegistration) {
      setProfessionalRegistration({
        type: registrationConfig.types[0],
        number: '',
      })
      setNoRegistration(false)
    }
  }, [
    countryCode,
    hasValidType,
    noRegistration,
    professionalRegistration,
    professionalRegistrations,
    registrationConfig,
    setNoRegistration,
    setProfessionalRegistration,
    setProfessionalRegistrations,
  ])

  if (!registrationConfig) {
    return null
  }

  const updateRegistration = (updates) => {
    const next = { ...registration, ...updates }
    setProfessionalRegistration(next)
  }

  return (
    <Form.Field>
      <Header as="h5">
        Professional Registration{' '}
        <Popup
          trigger={<Icon name="info circle" size="tiny" />}
          content={PROFESSIONAL_REGISTRATION_TOOLTIP}
        />
      </Header>
      <Label basic>{`Country: ${country}`}</Label>
      <p className="helper-text" style={{ marginTop: '1em' }}>
        {`Set by your Profile country. Not in ${country}? Update your `}
        <a href="/profile" onClick={goToProfileCountry}>
          Profile
        </a>{' '}
        to change it.
      </p>
      <Segment>
        <Form>
          {registrationConfig.types.length > 1 ? (
            <Form.Field>
              <label>{registrationConfig.label}</label>
              <Form.Group>
                <Form.Select
                  width={4}
                  label="ID type"
                  value={registration.type}
                  options={registrationConfig.types.map((t) => ({
                    key: t,
                    value: t,
                    text: t,
                  }))}
                  disabled={noRegistration}
                  onChange={(e, { value }) =>
                    updateRegistration({ type: value })
                  }
                />
                <Form.Input
                  width={12}
                  label="ID number"
                  placeholder={registrationConfig.placeholder}
                  value={registration.number}
                  disabled={noRegistration}
                  onChange={(e, { value }) =>
                    updateRegistration({ number: value })
                  }
                />
              </Form.Group>
            </Form.Field>
          ) : (
            <Form.Input
              label={registrationConfig.label}
              placeholder={registrationConfig.placeholder}
              value={registration.number}
              disabled={noRegistration}
              onChange={(e, { value }) => updateRegistration({ number: value })}
            />
          )}
          <p className="helper-text">{registrationConfig.helper}</p>
          <Form.Checkbox
            label="I don't have a professional registration ID"
            checked={noRegistration}
            onChange={(e, { checked }) => {
              setNoRegistration(checked)
              setProfessionalRegistration(
                checked
                  ? null
                  : { type: registrationConfig.types[0], number: '' }
              )
            }}
          />
        </Form>
      </Segment>
    </Form.Field>
  )
}

export default ProfessionalRegistration
